import { Link, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { BedDouble, TrainFront, CalendarDays, ClipboardList, Coins, Settings, ChevronRight, ArrowLeft, ShieldCheck, MapPin, Wallet } from "lucide-react";
import { useTrip } from "@/lib/store";
import { Switch } from "@/components/ui/switch";
import Stays from "./more/Stays";
import Transport from "./more/Transport";
import CalendarPage from "./more/CalendarPage";
import Organiser from "./more/Organiser";
import Costs from "./more/Costs";
import Expenses from "./more/Expenses";
import SettingsPage from "./more/SettingsPage";

const ITEMS = [
  { to: "expenses", label: "Expenses", sub: "Family spending log and receipts", Icon: Wallet },
  { to: "/map", label: "Map", sub: "The route and each city", Icon: MapPin },
  { to: "stays", label: "Stays", sub: "Hotels, addresses, Show Driver", Icon: BedDouble },
  { to: "transport", label: "Transport", sub: "Flights and trains", Icon: TrainFront },
  { to: "calendar", label: "Export calendar", sub: "Send days, stays or the trip to your personal calendar", Icon: CalendarDays },
  { to: "organiser", label: "Organiser notes", sub: "Checklist and private notes", Icon: ClipboardList, org: true },
  { to: "costs", label: "Booked / planned costs", sub: "Hotel totals and what's owed, from the spreadsheet", Icon: Coins, org: true },
  { to: "settings", label: "Settings", sub: "Currency, maps, offline copy", Icon: Settings },
];

function Menu() {
  const { organiser, setOrganiser, trip } = useTrip();
  const openTasks = trip.tasks.filter((t) => !t.done).length;
  const unpaid = trip.hotels.filter((h) => h.paymentStatus === "unpaid").length;
  return (
    <div className="pb-8">
      <p className="eyebrow mt-3">More</p>
      <h1 className="font-display text-3xl font-semibold">Everything else</h1>

      <div data-testid="organiser-view-toggle" className="card-soft mt-4 flex items-center justify-between p-4">
        <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--plum-soft)] text-[var(--plum)]"><ShieldCheck className="h-5 w-5" /></span>
          <div><p className="font-semibold">Organiser view</p><p className="text-xs text-[var(--ink-muted)]">Shows money, bookings to confirm and editing tools</p></div></div>
        <Switch data-testid="organiser-switch" checked={organiser} onCheckedChange={setOrganiser} className="data-[state=checked]:bg-[var(--plum)]" />
      </div>

      {organiser && unpaid > 0 && (
        <Link to="organiser" data-testid="payment-action-banner" className="mt-3 block rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 text-rose-900">
          <p className="eyebrow !text-rose-700">Payment action required</p>
          <p className="mt-1 font-semibold">Guangzhou hotel (J Residence) has not been paid according to the spreadsheet.</p>
        </Link>
      )}

      <ul className="mt-4 space-y-2">
        {ITEMS.filter((i) => !i.org || organiser).map(({ to, label, sub, Icon }) => (
          <li key={to}>
            <Link to={to} data-testid={`more-link-${to.replace("/", "")}`} className="tap card-soft flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--jade-soft)] text-[var(--jade)]"><Icon className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold">{label}{to === "organiser" && openTasks > 0 && <span className="ml-2 rounded-full bg-[var(--coral)] px-2 text-[10px] font-bold text-white">{openTasks}</span>}</span><span className="block text-xs text-[var(--ink-muted)]">{sub}</span></span>
              <ChevronRight className="h-4 w-4 text-[var(--ink-muted)]" />
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-8 text-center text-xs italic text-[var(--ink-muted)]">Original itinerary lovingly planned by Angelina</p>
    </div>
  );
}

export function SubHeader({ title, eyebrow }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="mt-3 flex items-center gap-3">
      <button data-testid="sub-back-btn" onClick={() => (pathname.split("/").length > 2 ? navigate("/more") : navigate(-1))} className="tap rounded-full bg-white p-2.5 border"><ArrowLeft className="h-4 w-4" /></button>
      <div><p className="eyebrow">{eyebrow}</p><h1 className="font-display text-3xl font-semibold leading-tight">{title}</h1></div>
    </div>
  );
}

export default function More() {
  return (
    <Routes>
      <Route index element={<Menu />} />
      <Route path="stays" element={<Stays />} />
      <Route path="transport" element={<Transport />} />
      <Route path="calendar" element={<CalendarPage />} />
      <Route path="organiser" element={<Organiser />} />
      <Route path="costs" element={<Costs />} />
      <Route path="expenses" element={<Expenses />} />
      <Route path="settings" element={<SettingsPage />} />
    </Routes>
  );
}
